import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, User, Hash, X, Printer } from "lucide-react";

const fmt = (n) =>
  (n ?? 0).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 1 });

export const StoricoRicerca = () => {
  const { t } = useTranslation("app");
  const [operai, setOperai] = useState([]);
  const [commesse, setCommesse] = useState([]);
  const [operaio, setOperaio] = useState("");
  const [codice, setCodice] = useState("");
  const [focus, setFocus] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/statistiche/storico/operai").then(({ data }) => setOperai(data || [])).catch(() => {});
    api.get("/statistiche/commesse-storiche").then(({ data }) => setCommesse(data || [])).catch(() => {});
  }, []);

  const opSuggest = useMemo(() => {
    const q = operaio.trim().toLowerCase();
    if (!q || q.length < 2) return [];
    return operai.filter((o) => o.operaio.toLowerCase().includes(q)).slice(0, 8);
  }, [operaio, operai]);

  const cmSuggest = useMemo(() => {
    const q = codice.trim().toLowerCase();
    if (!q || q.length < 2) return [];
    return commesse
      .filter((c) =>
        c.codice.toLowerCase().includes(q) ||
        (c.cliente || "").toLowerCase().includes(q) ||
        (c.descrizione || "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [codice, commesse]);

  const cerca = async (op = operaio, cod = codice) => {
    if (!op.trim() && !cod.trim()) return;
    setBusy(true);
    setFocus("");
    try {
      const { data } = await api.get("/statistiche/storico/ricerca", {
        params: { operaio: op.trim(), codice: cod.trim() },
      });
      setRes(data);
    } catch {
      setRes(null);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setOperaio(""); setCodice(""); setRes(null); };

  const [printing, setPrinting] = useState(false);
  const stampaPdf = async () => {
    if (!res?.trovato) return;
    setPrinting(true);
    try {
      const { data } = await api.get("/statistiche/storico/ricerca/pdf", {
        params: { operaio: res.operaio || "", codice: res.codice || "" },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `ricerca-storico${res.operaio ? "-" + res.operaio.replace(/\s+/g, "_") : ""}${res.codice ? "-" + res.codice : ""}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } finally {
      setPrinting(false);
    }
  };

  const maxAnno = res?.per_anno?.length
    ? Math.max(...res.per_anno.map((a) => a.ore), 1)
    : 1;

  return (
    <Card className="p-4 md:p-6 space-y-4" data-testid="storico-ricerca-section">
      <div>
        <div className="font-heading font-bold tracking-tight flex items-center gap-2">
          <Search className="h-5 w-5 text-[#0044FF]" /> {t("stats.ricercaTitle")}
        </div>
        <p className="text-sm text-slate-600 mt-1">{t("stats.ricercaSub")}</p>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <label className="text-xs font-bold text-slate-600 uppercase tracking-wider flex items-center gap-1">
            <User className="h-3 w-3" /> {t("stats.ricercaOperaio")}
          </label>
          <Input
            value={operaio}
            onChange={(e) => setOperaio(e.target.value)}
            onFocus={() => setFocus("op")}
            placeholder={t("stats.ricercaOperaioPh")}
            data-testid="storico-ricerca-operaio-input"
          />
          {focus === "op" && opSuggest.length > 0 && (
            <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-auto">
              {opSuggest.map((o) => (
                <button
                  key={o.operaio}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex justify-between gap-2"
                  onClick={() => { setOperaio(o.operaio); cerca(o.operaio, codice); }}
                  data-testid={`storico-op-suggest-${o.operaio.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <span className="font-semibold">{o.operaio}</span>
                  <span className="font-mono text-slate-500">{fmt(o.ore_tot)} h</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <label className="text-xs font-bold text-slate-600 uppercase tracking-wider flex items-center gap-1">
            <Hash className="h-3 w-3" /> {t("stats.ricercaCommessa")}
          </label>
          <Input
            value={codice}
            onChange={(e) => setCodice(e.target.value)}
            onFocus={() => setFocus("cm")}
            placeholder={t("stats.ricercaCommessaPh")}
            data-testid="storico-ricerca-commessa-input"
          />
          {focus === "cm" && cmSuggest.length > 0 && (
            <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-auto">
              {cmSuggest.map((c) => (
                <button
                  key={c.codice}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
                  onClick={() => { setCodice(c.codice); cerca(operaio, c.codice); }}
                  data-testid={`storico-cm-suggest-${c.codice.toLowerCase()}`}
                >
                  <span className="font-mono font-bold">{c.codice}</span>
                  <span className="text-slate-500 ml-2 text-xs">{c.cliente} {c.descrizione ? `— ${c.descrizione}` : ""}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <Button onClick={() => cerca()} disabled={busy || (!operaio.trim() && !codice.trim())} data-testid="storico-ricerca-btn">
          <Search className="h-4 w-4 mr-1" /> {t("stats.ricercaBtn")}
        </Button>
        {(res || operaio || codice) && (
          <Button variant="outline" onClick={reset} data-testid="storico-ricerca-reset-btn">
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {res && !res.trovato && (
        <p className="text-sm text-slate-500" data-testid="storico-ricerca-empty">
          {t("stats.ricercaNoRes")}
        </p>
      )}

      {res && res.trovato && (
        <div className="space-y-4" data-testid="storico-ricerca-results">
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={stampaPdf} disabled={printing} data-testid="storico-ricerca-pdf-btn">
              <Printer className="h-4 w-4 mr-1.5" /> {t("stats.ricercaPdfBtn")}
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
            <div className="rounded-md px-3 py-2 bg-[#0044FF] text-white">
              <div className="text-[10px] uppercase tracking-wider opacity-70">{t("stats.ricercaTot")}</div>
              <div className="font-mono font-bold text-lg" data-testid="storico-ricerca-ore-tot">{fmt(res.ore_tot)} h</div>
            </div>
            <div className="rounded-md px-3 py-2 bg-white border border-slate-200">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">{t("stats.ricercaRighe")}</div>
              <div className="font-mono font-bold text-lg">{fmt(res.n_righe)}</div>
            </div>
            <div className="rounded-md px-3 py-2 bg-white border border-slate-200 col-span-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">{t("stats.ricercaPeriodo")}</div>
              <div className="font-mono font-bold text-sm mt-1">
                {res.dal?.split("-").reverse().join("/")} → {res.al?.split("-").reverse().join("/")}
              </div>
            </div>
          </div>

          {res.per_anno?.length > 1 && (
            <div className="flex items-end gap-2 h-24" data-testid="storico-ricerca-anni-chart">
              {res.per_anno.map((a) => (
                <div key={a.anno} className="flex-1 flex flex-col items-center justify-end h-full gap-0.5">
                  <div className="font-mono text-[10px] font-bold text-slate-600">{fmt(a.ore)}</div>
                  <div className="w-full rounded-t bg-[#0044FF]/80" style={{ height: `${Math.max((a.ore / maxAnno) * 100, 4)}%` }} />
                  <div className="text-[10px] font-bold text-slate-700">{a.anno}</div>
                </div>
              ))}
            </div>
          )}

          {res.operaio && res.per_commessa?.length > 0 && (
            <div className="overflow-x-auto">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">{t("stats.ricercaPerCommessa")}</div>
              <table className="w-full text-sm" data-testid="storico-ricerca-commesse-table">
                <tbody>
                  {res.per_commessa.map((c) => (
                    <tr key={c.codice} className="border-b border-slate-100">
                      <td className="px-2 py-1.5 font-mono font-bold whitespace-nowrap">{c.codice}</td>
                      <td className="px-2 py-1.5 text-slate-500 text-xs">{c.cliente} {c.descrizione ? `— ${c.descrizione}` : ""}</td>
                      <td className="px-2 py-1.5 text-right font-mono font-bold whitespace-nowrap">{fmt(c.ore)} h</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!res.operaio && res.per_operaio?.length > 0 && (
            <div className="overflow-x-auto">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">{t("stats.ricercaPerOperaio")}</div>
              <table className="w-full text-sm" data-testid="storico-ricerca-operai-table">
                <tbody>
                  {res.per_operaio.map((o) => (
                    <tr key={o.operaio} className="border-b border-slate-100">
                      <td className="px-2 py-1.5 font-semibold">{o.operaio}</td>
                      <td className="px-2 py-1.5 text-right font-mono font-bold whitespace-nowrap">{fmt(o.ore)} h</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-xs text-slate-400">{t("stats.ricercaFonte")}</p>
        </div>
      )}
    </Card>
  );
};
